import { BodyRegular, HeadingThree } from 'components/Text';
import { CheckmarkIcon, ErrorIcon } from 'react-hot-toast';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { useEffect, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { IconButton } from 'components/Button';
import Modal from 'components/Modal';
import Stack from 'components/Stack';
import styled from 'styled-components';

const AlertIcon = ({ icon }) => {
    if (icon === 'success') {
        return <CheckmarkIcon />;
    }
    if (icon === 'error') {
        return <ErrorIcon />;
    }
    return null;
};

const DangerModeIconButton = styled(IconButton)`
    background-color: ${({ theme }) => theme.red_1};
    color: ${({ theme }) => theme.white};
    border: solid 1px ${({ theme }) => theme.red_2};
    &&:hover,
    &&:focus,
    &&:focus-visible {
        background-color: ${({ theme }) => theme.red_2};
        border-color: ${({ theme }) => theme.red_3};
    }
    &&:active {
        background-color: ${({ theme }) => theme.red_3};
        border-color: ${({ theme }) => theme.red_4};
    }
`;

const AlertButton = ({
    type,
    config,
    dangerMode,
    isDisabled,
    isLoading,
    confirm,
    cancel,
}) => {
    if (type === 'cancel') {
        return (
            <IconButton
                intent="secondary"
                onClick={() => {
                    cancel(config?.closeModal);
                }}
                disabled={isDisabled}
                fetching={isLoading}
            >
                {config || 'Cancel'}
            </IconButton>
        );
    } else if (type === 'confirm' && typeof config === 'string') {
        return (
            <IconButton
                intent="primary"
                onClick={() => confirm()}
                disabled={isDisabled}
                fetching={isLoading}
            >
                {config}
            </IconButton>
        );
    } else if (type === 'confirm' && typeof config === 'object') {
        const ButtonComponent = dangerMode ? DangerModeIconButton : IconButton;
        return (
            <ButtonComponent
                intent="primary"
                onClick={() => {
                    confirm(config?.closeModal);
                }}
                disabled={isDisabled}
                fetching={isLoading}
            >
                {config.text || 'Confirm'}
            </ButtonComponent>
        );
    }
    throw new Error(
        `Unknown button type or config: ${type}, ${JSON.stringify(config)}`,
    );
};

const DefaultAlert = NiceModal.create(
    ({ title, description, children, icon, buttons, dangerMode }) => {
        const modal = useModal();
        const [isLoading, setIsLoading] = useState(false);
        useEffect(() => {
            if (!modal.visible) setIsLoading(false);
            return () => {
                setIsLoading(false);
            };
        }, [modal.visible]);
        const confirm = (closeModal) => {
            setIsLoading(true);
            modal.resolve(true);
            if (closeModal) {
                modal.hide();
                setIsLoading(false);
            }
        };
        const cancel = (closeModal = true) => {
            setIsLoading(false);
            modal.resolve();
            if (closeModal) {
                modal.hide();
            }
        };
        if (!modal.visible) return null;
        return (
            <Modal static open={modal.visible} onClose={() => modal.hide()}>
                <Stack
                    css="padding: 1rem; display: flex; flex-direction: column; align-items: center;"
                    $space="1rem"
                >
                    {icon || title ? (
                        <span css="display: flex; align-items: center; gap: 1ch;">
                            {icon ? <AlertIcon icon={icon} /> : null}
                            {title ? (
                                <Dialog.Title as={HeadingThree}>
                                    {title}
                                </Dialog.Title>
                            ) : null}
                        </span>
                    ) : null}
                    {description ? (
                        <Dialog.Description>{description}</Dialog.Description>
                    ) : null}
                    {typeof children === 'string' ? (
                        <BodyRegular>{children}</BodyRegular>
                    ) : (
                        children
                    )}
                </Stack>
                {buttons ? (
                    <Stack as="footer" $space=".25rem" $direction="row">
                        {Object.entries(buttons).map(
                            ([buttonType, buttonConfig]) => {
                                return (
                                    <AlertButton
                                        type={buttonType}
                                        config={buttonConfig}
                                        dangerMode={dangerMode}
                                        key={buttonType}
                                        isDisabled={isLoading}
                                        isLoading={isLoading}
                                        confirm={confirm}
                                        cancel={cancel}
                                    />
                                );
                            },
                        )}
                    </Stack>
                ) : null}
            </Modal>
        );
    },
);

const spal = (
    children,
    { description = '', dangerMode = false, buttons, icon = null, title = '' },
) => {
    return NiceModal.show(DefaultAlert, {
        title,
        icon,
        buttons,
        dangerMode,
        description,
        children,
    });
};

export default spal;
